import { CircleCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import {
  Dialog as BaseDialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DonorSchedule } from '@/rtk-query.gql';

import { useMissingFields } from '../../hooks/useMissingFields';
import { MissingFieldsResult } from '../../types';

export const MissingFieldsDialog = ({
  visible,
  onHide,
  selectedDonorSchedules,
  renderFooter,
  variant = 'prime',
}: {
  visible: boolean;
  onHide: () => void;
  selectedDonorSchedules: DonorSchedule[];
  renderFooter?: () => React.ReactNode;
  variant?: 'prime' | 'base-ui';
}) => {
  const { t } = useTranslation();
  const { getMissingFields } = useMissingFields(selectedDonorSchedules);
  const missingFieldsData = getMissingFields() as MissingFieldsResult[];
  const isBaseUi = variant === 'base-ui';

  const content = (
    <div className="flex flex-col gap-4">
      {missingFieldsData.length === 0 ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            {isBaseUi ? (
              <CircleCheckIcon className="mx-auto mb-4 size-10 text-green-500" />
            ) : (
              <i className="pi pi-check-circle mb-4 text-4xl text-green-500"></i>
            )}
            <p className="text-lg font-semibold text-gray-700">
              {t('donorReport.allDonorsComplete')}
            </p>
            <p className="text-gray-500">
              {t('donorReport.noMissingFieldsFound')}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div>
            <p className="text-sm text-gray-600">
              {t('donorReport.foundDonorsWithMissingInfo', {
                count: missingFieldsData.length,
              })}
            </p>
          </div>

          <div className="max-h-[60vh] space-y-4 overflow-y-auto">
            {missingFieldsData.map((donor) => (
              <div
                key={donor.donorId}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {donor.donorName || t('donor.unknownDonor')}
                  </h3>
                  <span className="text-sm font-medium text-red-600">
                    {t('donorReport.missingFieldsCount', {
                      count: donor.missingFields.length,
                    })}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {donor.missingFields.map((field) => (
                    <div
                      key={field}
                      className="flex items-center gap-2 rounded border border-red-200 bg-white p-2"
                    >
                      {isBaseUi ? (
                        <TriangleAlertIcon className="size-4 shrink-0 text-red-500" />
                      ) : (
                        <i className="pi pi-exclamation-triangle text-sm text-red-500"></i>
                      )}
                      <span className="text-sm text-gray-700">{field}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  if (isBaseUi) {
    return (
      <BaseDialog open={visible} onOpenChange={(open) => !open && onHide()}>
        <DialogContent className="md:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('donorReport.missingFieldsReport')}</DialogTitle>
          </DialogHeader>
          <DialogBody>{content}</DialogBody>
          {renderFooter && <DialogFooter>{renderFooter()}</DialogFooter>}
        </DialogContent>
      </BaseDialog>
    );
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t('donorReport.missingFieldsReport')}
      footer={renderFooter && renderFooter()}
      className="max-w-[800px] min-w-[600px]"
    >
      {content}
    </Dialog>
  );
};
